import {
  useMemo,
  useState,
  useCallback,
} from "react";

import {
  COUNTRIES,
  getCountryList,
  getChannelsForCountry,
  getChannel,
  validateField,
  validatePixKey,
  getFieldErrorMessage,
} from "../data/corridorMap";

import { usePhoneNumber } from "./usePhoneNumber";

export function useCorridor() {
  // ===========================================================================
  // COUNTRY LIST
  // ===========================================================================

  const countries = useMemo(
    () => getCountryList(),
    []
  );

  // ===========================================================================
  // STATE
  // ===========================================================================

  const [countryCode, setCountryCodeState] =
    useState("");

  const [channelKey, setChannelKeyState] =
    useState("");

  const [values, setValues] =
    useState({});

  const [errors, setErrors] =
    useState({});

  // ===========================================================================
  // PHONE NUMBER
  // ===========================================================================

  /*
   * Phone validation is now centralized.
   *
   * It uses the currently selected corridor country.
   */
  const phoneNumber = usePhoneNumber(
    countryCode
  );

  // ===========================================================================
  // SELECTED COUNTRY
  // ===========================================================================

  const country = useMemo(() => {
    if (!countryCode) {
      return null;
    }

    return (
      COUNTRIES[countryCode] ?? null
    );
  }, [countryCode]);

  // ===========================================================================
  // AVAILABLE CHANNELS
  // ===========================================================================

  const availableChannels = useMemo(() => {
    if (!countryCode) {
      return {};
    }

    return (
      getChannelsForCountry(
        countryCode
      ) ?? {}
    );
  }, [countryCode]);

  // ===========================================================================
  // ACTIVE CHANNEL
  // ===========================================================================

  const activeChannel = useMemo(() => {
    if (
      !countryCode ||
      !channelKey
    ) {
      return null;
    }

    return getChannel(
      countryCode,
      channelKey
    );
  }, [
    countryCode,
    channelKey,
  ]);

  // ===========================================================================
  // RECIPIENT FIELDS
  // ===========================================================================

  const recipientFields = useMemo(
    () =>
      activeChannel
        ?.recipient_fields ?? [],
    [activeChannel]
  );

  // ===========================================================================
  // CHANNEL TYPE HELPERS
  // ===========================================================================

  const isMobileMoneyChannel =
    useMemo(
      () =>
        activeChannel?.channel ===
        "mobile_money",
      [activeChannel]
    );

  const isBankTransferChannel =
    useMemo(
      () =>
        activeChannel?.channel ===
        "bank_transfer",
      [activeChannel]
    );

  const isPixChannel =
    useMemo(
      () =>
        activeChannel?.channel === "pix",
      [activeChannel]
    );

  // ===========================================================================
  // COUNTRY SELECTION
  // ===========================================================================

  const setCountryCode = useCallback(
    (code) => {
      const normalizedCode =
        String(code || "")
          .trim()
          .toUpperCase();

      // Empty selection is valid.
      if (!normalizedCode) {
        setCountryCodeState("");
        setChannelKeyState("");
        setValues({});
        setErrors({});
        return;
      }

      // Never allow a country outside the map.
      if (!COUNTRIES[normalizedCode]) {
        return;
      }

      setCountryCodeState(
        normalizedCode
      );

      // Changing country invalidates
      // channel and recipient data.
      setChannelKeyState("");
      setValues({});
      setErrors({});
    },
    []
  );

  // ===========================================================================
  // CHANNEL SELECTION
  // ===========================================================================

  const setChannelKey = useCallback(
    (key) => {
      const normalizedKey =
        String(key || "").trim();

      // Allow clearing the channel.
      if (!normalizedKey) {
        setChannelKeyState("");
        setValues({});
        setErrors({});
        return;
      }

      // A channel cannot be selected
      // without a country.
      if (!countryCode) {
        return;
      }

      const channel =
        getChannel(
          countryCode,
          normalizedKey
        );

      // Never allow unsupported channels.
      if (!channel) {
        return;
      }

      setChannelKeyState(
        normalizedKey
      );

      // Changing payout method invalidates
      // old recipient data.
      setValues({});
      setErrors({});
    },
    [countryCode]
  );

  // ===========================================================================
  // MOBILE FIELD DETECTION
  // ===========================================================================

  const isMobileField = useCallback(
    (field) => {
      return (
        field?.key ===
          "mobile_number" ||
        field?.key ===
          "phone_number" ||
        field?.key === "mobile"
      );
    },
    []
  );

  // ===========================================================================
  // DIAL CODE
  // ===========================================================================

  const getDialCode = useCallback(() => {
    return country?.dialCode || "";
  }, [country]);

  // ===========================================================================
  // INTERNATIONAL -> LOCAL MOBILE NUMBER
  // ===========================================================================

  const getLocalMobileNumber =
    useCallback(
      (value) => {
        if (
          value === null ||
          value === undefined ||
          value === ""
        ) {
          return "";
        }

        /*
         * libphonenumber-js handles:
         *
         * +233241234567
         * 0241234567
         * 241234567
         *
         * and returns:
         *
         * 241234567
         */
        const normalized =
          phoneNumber.toNationalNumber(
            value,
            countryCode
          );

        if (normalized) {
          return normalized;
        }

        /*
         * Preserve the old behavior for an
         * incomplete value while the user is
         * typing.
         */
        const digits =
          String(value)
            .replace(/\D/g, "");

        if (!digits) {
          return "";
        }

        const dialDigits =
          String(getDialCode())
            .replace(/\D/g, "");

        if (
          dialDigits &&
          digits.startsWith(
            dialDigits
          )
        ) {
          return digits.slice(
            dialDigits.length
          );
        }

        if (
          digits.startsWith("0")
        ) {
          return digits.slice(1);
        }

        return digits;
      },
      [
        countryCode,
        getDialCode,
        phoneNumber,
      ]
    );

  // ===========================================================================
  // LOCAL -> INTERNATIONAL MOBILE NUMBER
  // ===========================================================================

  const getInternationalMobileNumber =
    useCallback(
      (value) => {
        if (
          value === null ||
          value === undefined ||
          value === ""
        ) {
          return "";
        }

        /*
         * This is now the canonical
         * normalization path.
         *
         * Example:
         *
         * 241234567
         *    ↓
         * +233241234567
         */
        const normalized =
          phoneNumber.toE164(
            value,
            countryCode
          );

        if (normalized) {
          return normalized;
        }

        /*
         * Fallback for incomplete values.
         *
         * This is useful while building a
         * payload from a partially-entered form,
         * although validation should prevent such
         * a payload from being submitted.
         */
        const dialCode =
          getDialCode();

        if (!dialCode) {
          return String(value).trim();
        }

        const digits =
          String(value)
            .replace(/\D/g, "");

        if (!digits) {
          return "";
        }

        const dialDigits =
          String(dialCode)
            .replace(/\D/g, "");

        if (
          dialDigits &&
          digits.startsWith(
            dialDigits
          )
        ) {
          return `+${digits}`;
        }

        const subscriberNumber =
          digits.startsWith("0")
            ? digits.slice(1)
            : digits;

        return `${dialCode}${subscriberNumber}`;
      },
      [
        countryCode,
        getDialCode,
        phoneNumber,
      ]
    );

  // ===========================================================================
  // INITIALIZE FROM EXISTING RECIPIENT / PAYMENT METHOD
  // ===========================================================================

  const initCorridor =
    useCallback(
      (
        code,
        key,
        initialValues = {}
      ) => {
        const normalizedCode =
          String(code || "")
            .trim()
            .toUpperCase();

        const normalizedKey =
          String(key || "").trim();

        const countryDefinition =
          COUNTRIES[
            normalizedCode
          ];

        // Invalid country -> reset.
        if (!countryDefinition) {
          setCountryCodeState("");
          setChannelKeyState("");
          setValues({});
          setErrors({});
          return;
        }

        const channelDefinition =
          countryDefinition.channels?.[
            normalizedKey
          ] ?? null;

        const incomingValues =
          initialValues &&
          typeof initialValues ===
            "object" &&
          !Array.isArray(
            initialValues
          )
            ? { ...initialValues }
            : {};

        /*
         * Persisted mobile numbers are stored
         * internationally.
         *
         * The UI operates in subscriber format.
         */
        if (
          channelDefinition
            ?.recipient_fields
        ) {
          for (
            const field of
              channelDefinition.recipient_fields
          ) {
            if (
              isMobileField(field)
            ) {
              incomingValues[
                field.key
              ] =
                /*
                 * IMPORTANT:
                 * We cannot rely on the hook's
                 * current country here because
                 * React state has not necessarily
                 * updated yet.
                 *
                 * Parse directly using the
                 * initialization country.
                 */
                phoneNumber.toNationalNumber(
                  incomingValues[
                    field.key
                  ],
                  normalizedCode
                );
            }
          }
        }

        setCountryCodeState(
          normalizedCode
        );

        setChannelKeyState(
          channelDefinition
            ? normalizedKey
            : ""
        );

        setValues(
          incomingValues
        );

        setErrors({});
      },
      [
        isMobileField,
        phoneNumber,
      ]
    );

  // ===========================================================================
  // SET FIELD VALUE
  // ===========================================================================

  const setValue = useCallback(
    (fieldKey, value) => {
      setValues((previous) => ({
        ...previous,
        [fieldKey]: value,
      }));

      // Clear this field's previous error.
      setErrors((previous) => {
        if (
          !(fieldKey in previous)
        ) {
          return previous;
        }

        const next = {
          ...previous,
        };

        delete next[fieldKey];

        return next;
      });
    },
    []
  );

  // ===========================================================================
  // MOBILE PROVIDER VALIDATION
  // ===========================================================================

  const validateMobileProvider =
    useCallback(
      (
        value,
        providerKey
      ) => {
        if (
          !isMobileMoneyChannel
        ) {
          return true;
        }

        if (!providerKey) {
          return false;
        }

        const provider =
          activeChannel
            ?.providers?.[
              providerKey
            ];

        if (!provider) {
          return false;
        }

        /*
         * The number itself is now validated
         * by libphonenumber-js.
         *
         * Provider validation remains our own
         * business rule.
         */
        const localNumber =
          getLocalMobileNumber(
            value
          );

        if (!localNumber) {
          return false;
        }

        const normalizedNumber =
          localNumber.replace(
            /\D/g,
            ""
          );

        const prefixes =
          Array.isArray(
            provider.msisdnPrefix
          )
            ? provider.msisdnPrefix
            : [];

        /*
         * If the corridor doesn't define
         * provider prefixes, selecting the
         * provider is sufficient.
         */
        if (!prefixes.length) {
          return true;
        }

        return prefixes.some(
          (prefix) => {
            const normalizedPrefix =
              String(prefix)
                .replace(/\D/g, "")
                .replace(/^0/, "");

            return normalizedNumber.startsWith(
              normalizedPrefix
            );
          }
        );
      },
      [
        activeChannel,
        getLocalMobileNumber,
        isMobileMoneyChannel,
      ]
    );

  // ===========================================================================
  // ENUM VALIDATION
  // ===========================================================================

  const validateEnumField =
    useCallback(
      (field, value) => {
        if (
          value === undefined ||
          value === null ||
          value === ""
        ) {
          return false;
        }

        if (
          !Array.isArray(
            field.values
          )
        ) {
          return true;
        }

        return field.values.includes(
          value
        );
      },
      []
    );

  // ===========================================================================
  // PIX VALIDATION
  // ===========================================================================

  const validatePixField =
    useCallback(
      (field, value) => {
        if (!isPixChannel) {
          return true;
        }

        if (
          field.key ===
          "pix_key_type"
        ) {
          return validateEnumField(
            field,
            value
          );
        }

        if (
          field.key === "pix_key"
        ) {
          const pixKeyType =
            values.pix_key_type;

          if (
            !pixKeyType ||
            !value
          ) {
            return false;
          }

          return validatePixKey(
            pixKeyType,
            String(value).trim()
          );
        }

        return true;
      },
      [
        isPixChannel,
        validateEnumField,
        values.pix_key_type,
      ]
    );

  // ===========================================================================
  // VALIDATE PHONE FIELD
  // ===========================================================================

  const validatePhoneField =
    useCallback(
      (value) => {
        if (!countryCode) {
          return false;
        }

        /*
         * This is now the important bit:
         *
         * We don't trust the old regex anymore.
         *
         * libphonenumber-js knows:
         *
         *   country
         *   numbering plan
         *   length
         *   valid ranges
         *   mobile classification
         */
        const valid =
          phoneNumber.validate(
            value,
            {
              country:
                countryCode,
              mobileOnly:
                isMobileMoneyChannel,
            }
          );

        if (!valid) {
          return false;
        }

        /*
         * For mobile-money corridors, retain
         * your existing provider/network check.
         */
        if (
          isMobileMoneyChannel
        ) {
          return validateMobileProvider(
            value,
            values.mobileProvider
          );
        }

        return true;
      },
      [
        countryCode,
        isMobileMoneyChannel,
        phoneNumber,
        validateMobileProvider,
        values.mobileProvider,
      ]
    );

  // ===========================================================================
  // VALIDATE SINGLE FIELD
  // ===========================================================================

  const validateSingleField =
    useCallback(
      (field, value) => {
        // Hidden fields don't require validation.
        if (
          field.type === "hidden"
        ) {
          return true;
        }

        // Optional fields can be empty.
        if (
          field.optional &&
          (
            value === undefined ||
            value === null ||
            value === ""
          )
        ) {
          return true;
        }

        // PIX-specific validation.
        if (isPixChannel) {
          if (
            field.key ===
              "pix_key_type" ||
            field.key ===
              "pix_key"
          ) {
            return validatePixField(
              field,
              value
            );
          }
        }

        // Enum fields.
        if (
          field.type === "enum"
        ) {
          return validateEnumField(
            field,
            value
          );
        }

        // Phone/mobile fields.
        if (
          isMobileField(field)
        ) {
          return validatePhoneField(
            value
          );
        }

        // Everything else continues to
        // use your existing corridor validation.
        return validateField(
          field,
          value
        );
      },
      [
        isMobileField,
        isPixChannel,
        validateEnumField,
        validatePhoneField,
        validatePixField,
      ]
    );

  // ===========================================================================
  // VALIDATE WHOLE CORRIDOR
  // ===========================================================================

  const validate = useCallback(
    () => {
      if (
        !countryCode ||
        !country ||
        !activeChannel
      ) {
        setErrors({
          _form:
            "Select a country and payout method.",
        });

        return false;
      }

      const nextErrors = {};

      for (
        const field of recipientFields
      ) {
        if (
          field.type === "hidden"
        ) {
          continue;
        }

        const value =
          values[field.key];

        if (
          !validateSingleField(
            field,
            value
          )
        ) {
          nextErrors[
            field.key
          ] =
            getFieldErrorMessage(
              field
            );
        }
      }

      setErrors(nextErrors);

      return (
        Object.keys(nextErrors)
          .length === 0
      );
    },
    [
      country,
      countryCode,
      activeChannel,
      recipientFields,
      values,
      validateSingleField,
    ]
  );

  // ===========================================================================
  // BUILD RECIPIENT FIELDS
  // ===========================================================================

  const buildRecipientFields =
    useCallback(
      () => {
        if (!activeChannel) {
          return {};
        }

        const payload = {};

        for (
          const field of
            recipientFields
        ) {
          let value;

          if (
            field.type === "hidden"
          ) {
            value = field.value;
          } else {
            value =
              values[field.key];
          }

          /*
           * Mobile numbers are ALWAYS
           * persisted internationally.
           */
          if (
            isMobileField(field)
          ) {
            value =
              getInternationalMobileNumber(
                value
              );
          }

          payload[field.key] =
            value ?? "";
        }

        return payload;
      },
      [
        activeChannel,
        getInternationalMobileNumber,
        isMobileField,
        recipientFields,
        values,
      ]
    );

  // ===========================================================================
  // BUILD PAYMENT METHOD PAYLOAD
  // ===========================================================================

  const buildPaymentMethodPayload =
    useCallback(
      () => {
        if (
          !country ||
          !countryCode ||
          !activeChannel
        ) {
          return null;
        }

        return {
          provider:
            activeChannel.psp,

          country_code:
            countryCode,

          currency:
            country.currency,

          channel:
            activeChannel.channel,

          network:
            isMobileMoneyChannel
              ? values.mobileProvider ||
                null
              : null,

          rail:
            activeChannel.rail ||
            null,

          recipient_fields:
            buildRecipientFields(),

          recipient_fields_schema_version:
            "v1",

          is_default: true,

          status: "active",
        };
      },
      [
        activeChannel,
        buildRecipientFields,
        country,
        countryCode,
        isMobileMoneyChannel,
        values.mobileProvider,
      ]
    );

  // ===========================================================================
  // FIELD VALUE HELPERS
  // ===========================================================================

  const getFieldValue =
    useCallback(
      (fieldKey) =>
        values[fieldKey] ?? "",
      [values]
    );

  const getFieldError =
    useCallback(
      (fieldKey) =>
        errors[fieldKey] ?? null,
      [errors]
    );

  // ===========================================================================
  // FORM VALIDITY
  // ===========================================================================

  const isValid = useMemo(() => {
    if (
      !country ||
      !activeChannel
    ) {
      return false;
    }

    if (errors._form) {
      return false;
    }

    for (
      const field of recipientFields
    ) {
      if (
        field.type === "hidden"
      ) {
        continue;
      }

      if (
        !validateSingleField(
          field,
          values[field.key]
        )
      ) {
        return false;
      }
    }

    return true;
  }, [
    country,
    activeChannel,
    errors._form,
    recipientFields,
    validateSingleField,
    values,
  ]);

  // ===========================================================================
  // RETURN API
  // ===========================================================================

  return {
    // -------------------------------------------------------------------------
    // Country
    // -------------------------------------------------------------------------

    countries,
    country,
    countryCode,
    setCountryCode,

    // -------------------------------------------------------------------------
    // Channels
    // -------------------------------------------------------------------------

    availableChannels,
    channelKey,
    setChannelKey,
    activeChannel,

    // -------------------------------------------------------------------------
    // Channel type helpers
    // -------------------------------------------------------------------------

    isMobileMoneyChannel,
    isBankTransferChannel,
    isPixChannel,

    // -------------------------------------------------------------------------
    // Recipient fields
    // -------------------------------------------------------------------------

    recipientFields,

    // -------------------------------------------------------------------------
    // Form state
    // -------------------------------------------------------------------------

    values,
    errors,
    isValid,

    setValue,
    getFieldValue,
    getFieldError,

    // -------------------------------------------------------------------------
    // Initialization
    // -------------------------------------------------------------------------

    initCorridor,

    // -------------------------------------------------------------------------
    // Validation
    // -------------------------------------------------------------------------

    validate,

    // -------------------------------------------------------------------------
    // Payload
    // -------------------------------------------------------------------------

    buildRecipientFields,
    buildPaymentMethodPayload,

    // -------------------------------------------------------------------------
    // Mobile helpers
    // -------------------------------------------------------------------------

    getDialCode,
    getLocalMobileNumber,
    getInternationalMobileNumber,

    // -------------------------------------------------------------------------
    // Phone utilities
    // -------------------------------------------------------------------------

    phoneNumber,
  };
}